import {useQuery, useQueryClient, UseQueryOptions, UseQueryResult} from '@tanstack/react-query';
import {queryClient} from '~/lib/queryClient';
import {fetchBidderGroup, fetchBidderGroups, fetchBidders, fetchSourcePreviews, IBiddersFilter} from '~/modules/Bidders/api';
import {IBidderGroup} from '~/modules/Bidders/models/BidderGroup';
import {IBidderSummary} from '~/modules/Bidders/models/BidderSummary';
import {ISourcePreview} from '~/modules/Sources/models/Source';

export const BIDDERS_QUERY_KEY = (filter: Pick<IBiddersFilter, 'active'> = {active: undefined}) =>
    ['bidders', {active: filter?.active ?? undefined}] as const;

export const useBiddersQuery = (active?: boolean | null) =>
    useQuery<IBidderSummary[]>({
        queryKey: BIDDERS_QUERY_KEY({active: typeof active === 'boolean' ? active : undefined}),
        queryFn: () => fetchBidders({active}),
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        placeholderData: (previousData) => previousData,
        refetchOnMount: false,
        refetchOnReconnect: false,
    });

export const prefetchBiddersQuery = (active?: boolean | null) =>
    queryClient.prefetchQuery({
        queryKey: BIDDERS_QUERY_KEY({active: typeof active === 'boolean' ? active : undefined}),
        queryFn: () => fetchBidders({active}),
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
    });

export const useInvalidateBiddersQuery = () => {
    const client = useQueryClient();
    return (active?: boolean | null) =>
        client.invalidateQueries({
            queryKey: BIDDERS_QUERY_KEY({active: typeof active === 'boolean' ? active : undefined}),
        });
};

export const BIDDER_GROUPS_QUERY_KEY_BASE = ['bidder-groups'] as const;

export const getBidderGroupsQueryKey = (filter: { id?: number; name?: string } = {}) =>
    [...BIDDER_GROUPS_QUERY_KEY_BASE, filter] as const;

export const BIDDER_GROUP_QUERY_KEY = (id: number | string | null | undefined) =>
    ['bidder-group', id] as const;

export const useBidderGroupsQuery = (
    filter: { id?: number; name?: string } = {},
    options?: Omit<UseQueryOptions<IBidderGroup[]>, 'queryKey' | 'queryFn'>
): UseQueryResult<IBidderGroup[]> =>
    useQuery<IBidderGroup[]>({
        queryKey: getBidderGroupsQueryKey(filter),
        queryFn: () => fetchBidderGroups(filter),
        keepPreviousData: true,
        ...options,
    });

export const useBidderGroupQuery = (
    id: number | null | undefined,
    options?: Omit<UseQueryOptions<IBidderGroup>, 'queryKey' | 'queryFn'>
): UseQueryResult<IBidderGroup> =>
    useQuery<IBidderGroup>({
        queryKey: BIDDER_GROUP_QUERY_KEY(id ?? null),
        queryFn: () => fetchBidderGroup(id!),
        enabled: typeof id === 'number' && !Number.isNaN(id),
        ...options,
    });

export const SOURCE_PREVIEWS_QUERY_KEY = ['source-previews'] as const;

export const useSourcePreviewsQuery = (
    options?: Omit<UseQueryOptions<ISourcePreview[]>, 'queryKey' | 'queryFn'>
): UseQueryResult<ISourcePreview[]> =>
    useQuery<ISourcePreview[]>({
        queryKey: SOURCE_PREVIEWS_QUERY_KEY,
        queryFn: fetchSourcePreviews,
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchOnWindowFocus: false,
        ...options,
    });

export const prefetchBidderGroup = (id: number) =>
    queryClient.prefetchQuery({
        queryKey: BIDDER_GROUP_QUERY_KEY(id),
        queryFn: () => fetchBidderGroup(id),
    });
